import { ComponentType, Dispatch, ReactNode, SetStateAction, useEffect } from 'react';
import { Size } from '@optimus-web/common';
import { usePrevious } from '@optimus-web/helpers';
import { AvatarProps } from '../Avatar';
import { Stack } from '../Stack';
import { MenuListItem, MenuListItemProps } from './MenuListItem';
import { NestedMenuList } from './NestedMenuList';
import { MenuListAvatar, MenuListElement, MenuListGroup, MenuListGroupDivider, MenuListGroupSeparator } from './style';

export interface MenuListProps<T> {
    items: MenuListItemProps<T>[];
    expandedItem?: string | null;
    onExpandableChange?: Dispatch<SetStateAction<string | null>>;
    activeItem?: string | null;
    onItemClick?: Dispatch<SetStateAction<string | null>>;
    label?: ReactNode;
    labelNumber?: number;
    separator?: boolean;
    collapsed?: boolean;
    size?: Omit<Size, 'small'>;
}

interface MenuListFactoryProps {
    MenuListItemAvatar?: ComponentType<AvatarProps>;
}

const renderLabel = (label?: ReactNode, collapsed?: boolean, number?: number) => {
    switch (true) {
        case label && !collapsed:
            return (
                <MenuListGroup>
                    <div>{label}</div>
                    {number ? <div>{number}</div> : null}
                </MenuListGroup>
            );
        case label && collapsed:
            return <MenuListGroupDivider />;
        default:
            return null;
    }
};

export const MenuListFactory = ({ MenuListItemAvatar = MenuListAvatar }: MenuListFactoryProps = {}) => {
    const MenuList = <T,>({
        items: parentItems,
        expandedItem,
        onExpandableChange,
        activeItem,
        onItemClick,
        label,
        labelNumber,
        separator,
        collapsed = false,
        size = 'medium',
    }: MenuListProps<T>) => {
        const prevCollapsed = usePrevious(collapsed);
        const prevActiveItem = usePrevious(activeItem);
        const itemExpanded = parentItems.find(({ id }) => id === expandedItem);
        const previousActiveInExpanded = !!itemExpanded?.items?.some(({ id }) => id === prevActiveItem);

        useEffect(() => {
            if (
                (!prevCollapsed && collapsed && expandedItem !== null) ||
                (collapsed && previousActiveInExpanded && prevActiveItem !== activeItem)
            ) {
                onExpandableChange?.(null);
            }
        }, [
            prevCollapsed,
            collapsed,
            expandedItem,
            onExpandableChange,
            activeItem,
            previousActiveInExpanded,
            prevActiveItem,
        ]);

        const handleExpandedItem = (id: string, expanded: boolean) => onExpandableChange?.(expanded ? null : id);
        const handleActiveItem = (id: string) => onItemClick?.(id);

        const menuListAvatar = (props: AvatarProps) => <MenuListItemAvatar {...props} size={40} />;

        return (
            <Stack vertical spacing="100">
                {renderLabel(label, collapsed, labelNumber)}
                <MenuListElement>
                    {parentItems.map(({ id, items, leftContent, ...others }) => {
                        const expandable = !!(items && items?.length);
                        const expanded = expandedItem === id;

                        return leftContent || !collapsed ? (
                            <MenuListItem
                                key={id}
                                onItemClick={
                                    expandable ? () => handleExpandedItem(id, expanded) : () => handleActiveItem(id)
                                }
                                expanded={expanded}
                                expandable={expandable}
                                collapsed={collapsed}
                                active={activeItem === id}
                                leftContent={leftContent}
                                size={size}
                                avatar={menuListAvatar}
                                {...others}
                            >
                                {expandable && items ? (
                                    <NestedMenuList
                                        items={items}
                                        onItemClick={handleActiveItem}
                                        activeItem={activeItem}
                                        expanded={expanded}
                                        collapsed={collapsed}
                                        label={label}
                                        number={labelNumber}
                                    />
                                ) : null}
                            </MenuListItem>
                        ) : null;
                    })}
                </MenuListElement>
                {separator ? <MenuListGroupSeparator /> : null}
            </Stack>
        );
    };
    return MenuList;
};

export const MenuList = MenuListFactory();
